import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Divider,
  Chip,
  CircularProgress,
  useTheme,
  Tooltip,
} from "@mui/material";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import BlockIcon from "@mui/icons-material/Block";
import SaveIcon from "@mui/icons-material/Save";
import { tokens } from "../../theme";
import { getPermisosOverride, getPermisosCatalogo, putPermisosOverride } from "../../services/permisosOverride.services";
import ModalUser from "../../modal/usuarios/modalUser";
import ModalSucces from "../../modal/modalSucces";
import ModalError from "../../modal/modalError";

const ALL_MODULES = [
  { code: "ADMIN",           label: "Administración" },
  { code: "SUPERVISOR",      label: "Supervisor" },
  { code: "CONTABILIDAD",    label: "Contabilidad" },
  { code: "RH",              label: "Recursos Humanos" },
  { code: "CALANDRA",        label: "Calandra" },
  { code: "STOCK",           label: "Stock" },
  { code: "LAVADORA",        label: "Lavadora" },
  { code: "SALA_LIMPIA",     label: "Sala Limpia" },
  { code: "INTERFOLIACION",  label: "Interfoliación" },
  { code: "MANTENIMIENTO",   label: "Mantenimiento" },
  { code: "AUX_RH",          label: "Aux. RRHH" },
  { code: "PUENTE_GRUA",     label: "Puente Grúa" },
  { code: "DIRECTIVOS",      label: "Directivos" },
  { code: "LABORATORIO",     label: "Laboratorio" },
  { code: "VENTA",           label: "Venta" },
  { code: "TEJIDO",          label: "Tejido" },
  { code: "TEJIDO_VENDEDOR", label: "Tejido Vendedor" },
  { code: "DEPOSITO",        label: "Depósito" },
];

const FALLBACK_CATALOG = ALL_MODULES.map((module) => ({
  code: module.code,
  label: module.label,
  children: [],
}));

const normalizeCatalog = (payload) => {
  const modules = Array.isArray(payload)
    ? payload
    : payload?.modules || payload?.data || [];

  if (!Array.isArray(modules) || modules.length === 0) {
    return FALLBACK_CATALOG;
  }

  return modules.map((module) => ({
    code: String(module.code || module.key || "").toUpperCase(),
    label: module.label || module.nombre || module.name || module.code,
    children: Array.isArray(module.children || module.items || module.permissions)
      ? (module.children || module.items || module.permissions).map((item) => ({
          code: String(item.code || item.key || "").toUpperCase(),
          label: item.label || item.nombre || item.name || item.code,
        })).filter((item) => item.code)
      : [],
  })).filter((module) => module.code);
};

const LEVEL_LABEL = {
  1: "ADMIN", 2: "SUPERVISOR", 4: "RH", 5: "CALANDRA",
  6: "STOCK", 7: "LAVADORA", 8: "SALA_LIMPIA", 9: "INTERFOLIACION",
  10: "MANTENIMIENTO", 11: "AUX_RH", 12: "PUENTE_GRUA", 13: "CONTABILIDAD",
  15: "DIRECTIVOS", 16: "LABORATORIO", 17: "VENTA", 18: "TEJIDO",
  19: "TEJIDO_VENDEDOR", 20: "DEPOSITO",
};

const STATE = { NONE: "NONE", ADD: "ADD", REMOVE: "REMOVE" };

const PermisosUsuario = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [openUserModal, setOpenUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [catalog, setCatalog] = useState(FALLBACK_CATALOG);

  // Por módulo: NONE | ADD | REMOVE
  const [moduleStates, setModuleStates] = useState({});

  useEffect(() => {
    const fetchCatalog = async () => {
      try {
        const data = await getPermisosCatalogo();
        setCatalog(normalizeCatalog(data));
      } catch (error) {
        setCatalog(FALLBACK_CATALOG);
      }
    };

    fetchCatalog();
  }, []);

  const baseModuleCode = selectedUser ? LEVEL_LABEL[selectedUser.id_level] : null;

  const handleSelectUser = useCallback(async (user) => {
    setSelectedUser(user);
    setModuleStates({});
    setLoading(true);
    try {
      const data = await getPermisosOverride(user.id);
      const initial = {};
      (data.add || []).forEach((code) => { initial[code] = STATE.ADD; });
      (data.remove || []).forEach((code) => { initial[code] = STATE.REMOVE; });
      setModuleStates(initial);
    } catch (err) {
      setErrorMsg(err.message);
      setShowError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const cycleState = (code) => {
    // No se puede cambiar el estado del módulo base del nivel
    if (code === baseModuleCode) return;

    setModuleStates((prev) => {
      const current = prev[code] || STATE.NONE;
      const next =
        current === STATE.NONE   ? STATE.ADD :
        current === STATE.ADD    ? STATE.REMOVE :
        STATE.NONE;
      return { ...prev, [code]: next };
    });
  };

  const handleSave = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      const add = [];
      const remove = [];
      Object.entries(moduleStates).forEach(([code, state]) => {
        if (state === STATE.ADD) add.push(code);
        if (state === STATE.REMOVE) remove.push(code);
      });
      await putPermisosOverride(selectedUser.id, { add, remove });
      setShowSuccess(true);
    } catch (err) {
      setErrorMsg(err.message);
      setShowError(true);
    } finally {
      setSaving(false);
    }
  };

  const getChipStyle = (code) => {
    const state = moduleStates[code] || STATE.NONE;
    const isBase = code === baseModuleCode;

    if (isBase) {
      return {
        backgroundColor: colors.blueAccent[700],
        color: "#fff",
        border: `2px solid ${colors.blueAccent[400]}`,
        cursor: "default",
      };
    }
    if (state === STATE.ADD) {
      return {
        backgroundColor: "rgba(76, 175, 80, 0.2)",
        color: colors.greenAccent[300],
        border: "2px solid #4caf50",
        cursor: "pointer",
      };
    }
    if (state === STATE.REMOVE) {
      return {
        backgroundColor: "rgba(244, 67, 54, 0.15)",
        color: "#f44336",
        border: "2px solid #f44336",
        cursor: "pointer",
      };
    }
    return {
      backgroundColor: colors.primary[400],
      color: colors.grey[300],
      border: `1px solid ${colors.grey[600]}`,
      cursor: "pointer",
    };
  };

  const getLegendIcon = (code) => {
    const state = moduleStates[code] || STATE.NONE;
    const isBase = code === baseModuleCode;
    if (isBase) return null;
    if (state === STATE.ADD) return <AddCircleOutlineIcon sx={{ fontSize: 14, mr: 0.5 }} />;
    if (state === STATE.REMOVE) return <BlockIcon sx={{ fontSize: 14, mr: 0.5 }} />;
    return null;
  };

  return (
    <Box m="20px">
      <Typography variant="h4" fontWeight="bold" color={colors.grey[100]} mb={1}>
        Permisos por Usuario
      </Typography>
      <Typography variant="body2" color={colors.grey[400]} mb={3}>
        Asigná permisos adicionales o bloqueá módulos para un usuario específico sin cambiar su nivel.
      </Typography>

      {/* Selector de usuario */}
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Button
          variant="contained"
          startIcon={<PersonSearchIcon />}
          onClick={() => setOpenUserModal(true)}
          sx={{
            backgroundColor: colors.blueAccent[700],
            "&:hover": { backgroundColor: colors.blueAccent[600] },
          }}
        >
          {selectedUser ? "Cambiar Usuario" : "Seleccionar Usuario"}
        </Button>

        {selectedUser && (
          <Box
            sx={{
              px: 2, py: 1,
              backgroundColor: colors.primary[400],
              borderRadius: 1,
              border: `1px solid ${colors.grey[600]}`,
            }}
          >
            <Typography variant="body1" color={colors.grey[100]}>
              <strong>{selectedUser.firstname} {selectedUser.lastname}</strong>
              &nbsp;—&nbsp;
              <span style={{ color: colors.grey[400] }}>
                Nivel {selectedUser.id_level}
                {baseModuleCode ? ` (${baseModuleCode})` : ""}
              </span>
            </Typography>
          </Box>
        )}
      </Box>

      {loading && (
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <CircularProgress size={20} />
          <Typography variant="body2" color={colors.grey[400]}>
            Cargando permisos...
          </Typography>
        </Box>
      )}

      {/* Panel de módulos */}
      {selectedUser && !loading && (
        <>
          <Divider sx={{ mb: 2, borderColor: colors.grey[700] }} />

          {/* Leyenda */}
          <Box display="flex" gap={2} mb={2} flexWrap="wrap">
            <Box display="flex" alignItems="center" gap={0.5}>
              <Box sx={{ width: 14, height: 14, borderRadius: "50%", backgroundColor: colors.blueAccent[700] }} />
              <Typography variant="caption" color={colors.grey[400]}>
                Módulo base del nivel (no editable)
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={0.5}>
              <AddCircleOutlineIcon sx={{ fontSize: 14, color: "#4caf50" }} />
              <Typography variant="caption" color={colors.grey[400]}>ADD — permiso extra activo</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={0.5}>
              <BlockIcon sx={{ fontSize: 14, color: "#f44336" }} />
              <Typography variant="caption" color={colors.grey[400]}>DENY — permiso bloqueado</Typography>
            </Box>
            <Typography variant="caption" color={colors.grey[500]}>
              Clic en un módulo para ciclar: sin cambio → ADD → DENY → sin cambio
            </Typography>
          </Box>

          {/* Grid de chips por modulo y apartados */}
          <Box
            mb={3}
            sx={{
              maxHeight: "52vh",
              overflowY: "auto",
              pr: 1,
              borderRadius: 1,
              "&::-webkit-scrollbar": {
                width: "10px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: colors.primary[500],
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: colors.blueAccent[700],
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: colors.blueAccent[600],
              },
            }}
          >
            {catalog.map((module) => (
              <Box key={module.code} mb={2}>
                <Typography variant="caption" color={colors.grey[500]} sx={{ display: "block", mb: 1 }}>
                  {module.label}
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1.2}>
                  <Tooltip
                    title={
                      module.code === baseModuleCode
                        ? "Módulo base del nivel (no modificable aquí)"
                        : `Click para cambiar estado de ${module.label}`
                    }
                    arrow
                  >
                    <Chip
                      icon={getLegendIcon(module.code)}
                      label={module.label}
                      onClick={() => cycleState(module.code)}
                      sx={{
                        ...getChipStyle(module.code),
                        fontWeight: module.code === baseModuleCode ? "bold" : "normal",
                        transition: "all 0.2s",
                        "&:hover": module.code === baseModuleCode ? {} : { opacity: 0.85 },
                      }}
                    />
                  </Tooltip>
                  {module.children.map((child) => (
                    <Tooltip
                      key={child.code}
                      title={`Permiso puntual: ${child.label}`}
                      arrow
                    >
                      <Chip
                        icon={getLegendIcon(child.code)}
                        label={child.label}
                        onClick={() => cycleState(child.code)}
                        sx={{
                          ...getChipStyle(child.code),
                          borderStyle: "dashed",
                          transition: "all 0.2s",
                          "&:hover": { opacity: 0.85 },
                        }}
                      />
                    </Tooltip>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>

          {/* Resumen */}
          <Box
            sx={{
              p: 2,
              mb: 3,
              backgroundColor: colors.primary[400],
              borderRadius: 1,
              border: `1px solid ${colors.grey[700]}`,
            }}
          >
            <Typography variant="body2" color={colors.grey[300]} mb={0.5}>
              <strong>ADD:</strong>{" "}
              {Object.entries(moduleStates)
                .filter(([, s]) => s === STATE.ADD)
                .map(([c]) => c)
                .join(", ") || "ninguno"}
            </Typography>
            <Typography variant="body2" color={colors.grey[300]}>
              <strong>DENY:</strong>{" "}
              {Object.entries(moduleStates)
                .filter(([, s]) => s === STATE.REMOVE)
                .map(([c]) => c)
                .join(", ") || "ninguno"}
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            sx={{
              backgroundColor: colors.greenAccent[600],
              "&:hover": { backgroundColor: colors.greenAccent[500] },
            }}
          >
            {saving ? "Guardando..." : "Guardar Permisos"}
          </Button>
        </>
      )}

      {/* Modales */}
      <ModalUser
        open={openUserModal}
        onClose={() => setOpenUserModal(false)}
        onSelectUsuario={handleSelectUser}
      />

      {showSuccess && (
        <ModalSucces
          open={showSuccess}
          onClose={() => setShowSuccess(false)}
          message="Permisos guardados correctamente."
        />
      )}

      {showError && (
        <ModalError
          open={showError}
          onClose={() => setShowError(false)}
          message={errorMsg}
        />
      )}
    </Box>
  );
};

export default PermisosUsuario;
